import { useRef } from 'react'
import { useAppLock } from '../hooks/use-app-lock'
import { LockIcon } from './lock-icon'
import { BRAND_LOGO_URL } from '../../../utils/brand-logo'
import { SOFT_LOCK_VERIFY_COPY } from '../session-security-policy'
import { APP_LOCK_Z_CLASS } from '../../../utils/call-overlay-layers'
import { useFocusTrap } from '../../../hooks/use-focus-trap'

/**
 * Soft-lock unlock sheet ("Verify it's you").
 *
 * Mounted once near the app root (`NativeBootstrap`). Sits at z-[50] so voice/
 * video call overlays (z-[60]) and Switch stay tappable while soft-locked —
 * incoming Answer and mid-call Switch are not blocked (13B / 19D).
 *
 * The dimmer is a real modal: it captures pointer/touch (the previous
 * `pointer-events-none` root let Messages stay tappable — an auth bypass).
 * Call UI stays above this layer via z-index, not by punching a hole in hit-testing.
 *
 * Shell remains visible underneath (blurred lists handle content redaction).
 * Native-only; web MFA is a separate slice.
 */
export function AppLockScreen() {
  const {
    showSheet,
    checking,
    failed,
    errorMessage,
    label,
    needsEnroll,
    optionalPrompt,
    postCallHold,
    attemptUnlock,
    dismissOptionalPrompt,
    acceptOptionalPrompt,
    signOut,
  } = useAppLock()
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, showSheet || Boolean(optionalPrompt && !showSheet))

  // L1/L2 one-shot soft prompt (29B) — light stub UI.
  if (optionalPrompt && !showSheet) {
    return (
      <div
        className={`pointer-events-auto fixed inset-0 ${APP_LOCK_Z_CLASS} flex items-end justify-center bg-slate-900/25 p-4 pb-[max(1rem,var(--safe-bottom))] backdrop-blur-[2px] sm:items-center native-modal-top touch-none`}
        role="dialog"
        aria-modal="true"
        aria-label="Optional biometric unlock"
        ref={dialogRef}
      >
        <div className="pointer-events-auto w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
          <h2 className="text-base font-semibold text-slate-900">Lock with {label}?</h2>
          <p className="mt-1 text-sm text-slate-600">
            Optionally require {label} after 12 hours away. You can change this later in settings.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={dismissOptionalPrompt}
              className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={() => void acceptOptionalPrompt()}
              disabled={checking}
              className="flex-1 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {checking ? 'Waiting\u2026' : 'Enable'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!showSheet) return null

  return (
    <div
      className={`pointer-events-auto fixed inset-0 ${APP_LOCK_Z_CLASS} flex items-end justify-center bg-slate-900/25 p-4 pb-[max(1rem,var(--safe-bottom))] backdrop-blur-[2px] sm:items-center native-modal-top touch-none`}
      role="dialog"
      aria-modal="true"
      aria-label={SOFT_LOCK_VERIFY_COPY}
      ref={dialogRef}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="pointer-events-auto w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
        <img
          src={BRAND_LOGO_URL}
          alt=""
          className="mx-auto h-12 w-12 rounded-2xl bg-brand-50 p-1.5"
          aria-hidden
        />
        <div
          className="mx-auto mt-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-700"
          aria-hidden
        >
          {checking ? (
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
          ) : (
            <LockIcon className="h-7 w-7" />
          )}
        </div>
        <h1 className="mt-4 text-lg font-bold text-slate-900">{SOFT_LOCK_VERIFY_COPY}</h1>
        <p className="mt-1 text-sm text-slate-600" role={failed ? 'alert' : 'status'} aria-live="polite">
          {checking
            ? `Waiting for ${label}\u2026`
            : needsEnroll
              ? `Enable ${label} to protect your session on this device.`
              : failed
                ? errorMessage ?? `${label} check didn\u2019t pass. Try again or use your password.`
                : postCallHold
                  ? `Your call has ended. Unlock Ascend to continue with ${label} or your device passcode.`
                  : `Unlock Ascend to continue. Use ${label}, or your device passcode.`}
        </p>

        <div className="mt-5 flex w-full flex-col gap-2">
          <button
            type="button"
            onClick={() => void attemptUnlock()}
            disabled={checking}
            className="w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
            aria-label={
              checking
                ? 'Waiting for biometrics'
                : needsEnroll
                  ? `Enable ${label}`
                  : failed
                    ? 'Try again'
                    : `Unlock with ${label}`
            }
          >
            {checking
              ? 'Waiting\u2026'
              : needsEnroll
                ? `Enable ${label}`
                : failed
                  ? 'Try again'
                  : `Unlock with ${label}`}
          </button>
          <button
            type="button"
            onClick={() => void signOut()}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Use password instead
          </button>
        </div>
      </div>
    </div>
  )
}
